import type {
  SoftphoneDialpadStoreState,
  UseSoftphoneDialpadReturn,
} from './index.types';
import {
  DISCARDED_DIALPAD_INPUT_CHARACTERS,
  INVALID_DIALPAD_INPUT_CHARACTERS,
} from 'Modules/softphone/components/constants';
import { useSoftphoneCallControls } from 'Modules/softphone/hooks';
import {
  useSoftphoneStore,
  type SoftphoneStore,
} from 'Modules/softphone/store';
import { createLogger } from 'Modules/softphone/utils/logger';
import { useCallback, useEffect, useLayoutEffect } from 'react';
import { pushToGTMDataLayer } from 'Utils/analytics';

const logger = createLogger('useSoftphoneDialpad');

/**
 * When the caret is at the end, align horizontal scroll once (dial-pad path).
 */
function scrollTrailingEdgeIfNeeded(
  input: HTMLInputElement,
  caretPosition: number,
  valueLength: number
): void {
  if (caretPosition !== valueLength || valueLength === 0) {
    return;
  }
  try {
    const rtl = getComputedStyle(input).direction === 'rtl';
    input.scrollLeft = rtl ? 0 : input.scrollWidth;
  } catch {
    /* non-DOM mocks */
  }
}

/**
 * useSoftphoneDialpad Hook
 * Manages dialpad input state with full editing capabilities.
 * The text field is registered via `dialpadTextFieldRef` so grid + input share one DOM node (Zustand slice).
 */
export const useSoftphoneDialpad = (): UseSoftphoneDialpadReturn => {
  const { handleCall } = useSoftphoneCallControls();

  const {
    dialpadDigits,
    cursorPosition,
    setDialpadDigits,
    setCursorPosition,
    setCursorSelectionEnd,
    setDialpadActive,
    registerDialpadTextField,
    handleDialpadDigitPress,
  } = useSoftphoneStore(
    (state: SoftphoneStore): SoftphoneDialpadStoreState => ({
      dialpadDigits: state.dialpadDigits,
      cursorPosition: state.cursorPosition,
      setDialpadDigits: state.setDialpadDigits,
      setDialpadActive: state.setDialpadActive,
      setCursorPosition: state.setCursorPosition,
      setCursorSelectionEnd: state.setCursorSelectionEnd,
      registerDialpadTextField: state.registerDialpadTextField,
      handleDialpadDigitPress: state.handleDialpadDigitPress,
    })
  );

  /**
   * Auto-focus input when component mounts
   */
  useEffect(() => {
    const el = useSoftphoneStore.getState().getDialpadTextField();
    el?.focus();
  }, []);

  /**
   * Legacy TopBarDialpad: when the number changes and focus is on the grid (not the
   * input), return focus + caret to the field before paint. If the input is already
   * focused (physical keyboard), skip — refocus/setSelection every keystroke caused a
   * visible flash.
   */
  useLayoutEffect(() => {
    const el = useSoftphoneStore.getState().getDialpadTextField();
    if (!el) {
      return;
    }
    if (document.activeElement === el) {
      return;
    }
    const { dialpadDigits: digits, cursorPosition: posRaw } =
      useSoftphoneStore.getState();
    const len = digits.length;
    if (len === 0) {
      return;
    }
    const pos =
      posRaw !== null ? Math.min(Math.max(0, posRaw), len) : len;
    el.focus();
    el.setSelectionRange(pos, pos);
    scrollTrailingEdgeIfNeeded(el, pos, len);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: sync only on value change (see legacy useEffect([inputNumber]))
  }, [dialpadDigits]);

  /**
   * Filter invalid characters from input
   */
  const filterInput = useCallback((input: string): string => {
    return input.replace(INVALID_DIALPAD_INPUT_CHARACTERS, '');
  }, []);

  /**
   * Handle input change
   */
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = filterInput(e.target.value);
      setDialpadDigits(newValue);

      // Update cursor position after filtering
      const cursorPos = e.target.selectionStart;
      if (cursorPos !== null) {
        setCursorPosition(cursorPos);
        setCursorSelectionEnd(cursorPos);
      }
    },
    [filterInput, setDialpadDigits, setCursorPosition, setCursorSelectionEnd]
  );

  /**
   * Handle call initiation
   */
  const handleCallInitiate = useCallback(
    (number: string) => {
      if (!number.trim()) {
        logger.warn('Cannot initiate call: empty number');
        return;
      }

      // Clean number by removing formatting characters
      const cleanedNumber = number.replace(
        DISCARDED_DIALPAD_INPUT_CHARACTERS,
        ''
      );

      logger.info('Initiating call', { number: cleanedNumber });

      // Track analytics event
      pushToGTMDataLayer('dialpad_call_start', {
        number_length: cleanedNumber.length,
      });

      void handleCall(cleanedNumber);
    },
    [handleCall]
  );

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle Enter key to initiate call
      if (e.key === 'Enter' && dialpadDigits.trim().length > 0) {
        e.preventDefault();
        handleCallInitiate(dialpadDigits);
      }
    },
    [dialpadDigits, handleCallInitiate]
  );

  /**
   * Track cursor position on selection change
   */
  const handleSelect = useCallback(
    (e: React.SyntheticEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      setCursorPosition(target.selectionStart);
      setCursorSelectionEnd(target.selectionEnd);
    },
    [setCursorPosition, setCursorSelectionEnd]
  );

  /**
   * Persist caret when focus leaves the field (legacy onBlur on dialpad input).
   */
  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const start = e.target.selectionStart;
      const end = e.target.selectionEnd;
      if (start !== null) {
        setCursorPosition(start);
      }
      if (end !== null) {
        setCursorSelectionEnd(end);
      }
    },
    [setCursorPosition, setCursorSelectionEnd]
  );

  /**
   * Handle paste events with validation
   */
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>) => {
      e.preventDefault();

      const pastedText = e.clipboardData.getData('text');
      const filteredText = filterInput(pastedText);

      if (!filteredText) return;

      const target = e.target as HTMLInputElement;
      const start = target.selectionStart ?? dialpadDigits.length;
      const end = target.selectionEnd ?? dialpadDigits.length;

      // Insert filtered text at cursor position
      const newValue =
        dialpadDigits.substring(0, start) + filteredText + dialpadDigits.substring(end);
      setDialpadDigits(newValue);

      // Set cursor after pasted text
      const newCursorPos = start + filteredText.length;
      setCursorPosition(newCursorPos);
      setCursorSelectionEnd(newCursorPos);

      queueMicrotask(() => {
        const inputEl = useSoftphoneStore.getState().getDialpadTextField();
        if (inputEl && document.activeElement === inputEl) {
          inputEl.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    },
    [dialpadDigits, filterInput, setDialpadDigits, setCursorPosition, setCursorSelectionEnd]
  );

  /**
   * Handle backspace button - removes the character before the cursor position,
   * mirroring native keyboard backspace behaviour regardless of whether the
   * deletion is triggered by the physical key or the on-screen button.
   */
  const handleBackspace = useCallback(() => {
    if (dialpadDigits.length === 0) {
      return;
    }

    const input = useSoftphoneStore.getState().getDialpadTextField();
    const len = dialpadDigits.length;
    const { cursorSelectionEnd: storedEnd } = useSoftphoneStore.getState();

    // Determine effective selection range from the DOM (if focused) or stored state.
    let start: number;
    let end: number;
    const domStart = input?.selectionStart ?? null;
    const domEnd = input?.selectionEnd ?? null;
    if (input && document.activeElement === input && domStart !== null) {
      start = domStart;
      end = domEnd !== null ? domEnd : start;
    } else {
      start = cursorPosition !== null ? Math.min(Math.max(0, cursorPosition), len) : len;
      end = storedEnd !== null ? Math.min(Math.max(0, storedEnd), len) : start;
    }

    let newValue: string;
    let newCursorPos: number;

    if (start !== end) {
      // There is a selection — delete the selected range.
      newValue = dialpadDigits.substring(0, start) + dialpadDigits.substring(end);
      newCursorPos = start;
    } else {
      // No selection — delete the character before the cursor.
      if (start === 0) {
        return;
      }
      newValue = dialpadDigits.substring(0, start - 1) + dialpadDigits.substring(start);
      newCursorPos = start - 1;
    }

    setDialpadDigits(newValue);
    setCursorPosition(newCursorPos);
    setCursorSelectionEnd(newCursorPos);

    // Sync the DOM caret so subsequent presses stay consistent.
    queueMicrotask(() => {
      const el = useSoftphoneStore.getState().getDialpadTextField();
      if (el && document.activeElement === el) {
        el.setSelectionRange(newCursorPos, newCursorPos);
      }
    });
  }, [dialpadDigits, cursorPosition, setDialpadDigits, setCursorPosition, setCursorSelectionEnd]);

  /**
   * Clear input — delegates to the slice's clearDialpadDigits (single source of truth)
   * and refocuses the text field.
   */
  const clearInput = useCallback(() => {
    useSoftphoneStore.getState().clearDialpadDigits();
    useSoftphoneStore.getState().getDialpadTextField()?.focus();
  }, []);

  /**
   * Handle dialpad close - deactivate dialpad (digits are cleared automatically)
   */
  const handleClose = useCallback(() => {
    setDialpadActive(false);
  }, [setDialpadActive]);

  /**
   * Validate input (1-21 characters, excluding formatting)
   */
  const isValid = dialpadDigits.trim().length > 0 && dialpadDigits.trim().length <= 21;

  return {
    dialpadDigits,
    cursorPosition,
    handleChange,
    handleKeyDown,
    handleSelect,
    handleBlur,
    handlePaste,
    handleDigitPress: handleDialpadDigitPress,
    handleBackspace,
    handleClose,
    dialpadTextFieldRef: registerDialpadTextField,
    clearInput,
    isValid,
  };
};
